import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import CaseAutocomplete from '../Common/CaseAutocomplete';
import { getAuthToken } from '../../lib/authToken';

interface CreateFromUnbilledDialogProps {
  open: boolean;
  onClose: () => void;
  /** Prefill when opened from a billable case row */
  defaultCaseId?: string;
  defaultClientId?: string;
}

const CreateFromUnbilledDialog: React.FC<CreateFromUnbilledDialogProps> = ({
  open,
  onClose,
  defaultCaseId = '',
  defaultClientId = '',
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [caseId, setCaseId] = useState(defaultCaseId);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open) {
      setCaseId(defaultCaseId || '');
      setSelectedIds([]);
      setNotes('');
    }
  }, [open, defaultCaseId, defaultClientId]);

  const { data: items = [], isLoading, error } = useQuery({
    queryKey: ['unbilled-approved', caseId],
    queryFn: async () => {
      const params = new URLSearchParams({
        status: 'approved',
        limit: '200',
      });
      const res = await fetch(`/api/case-billing-items?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Unable to load unbilled items'));
      const json = await res.json();
      const all = json.data?.billingItems || [];
      if (!caseId) return all;
      return all.filter((item: any) => item.caseId === caseId || item.case?.id === caseId);
    },
    enabled: open,
  });

  const selectable = useMemo(
    () => items.filter((item: any) => item.billable !== false),
    [items]
  );

  const selectedTotal = useMemo(
    () =>
      selectable
        .filter((item: any) => selectedIds.includes(item.id))
        .reduce((sum: number, item: any) => sum + Number(item.amount || 0), 0),
    [selectable, selectedIds]
  );

  const allSelected =
    selectable.length > 0 && selectable.every((item: any) => selectedIds.includes(item.id));

  const toggleAll = () => {
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(selectable.map((item: any) => item.id));
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        billingItemIds: selectedIds,
        notes: notes || undefined,
      };
      if (caseId) body.caseId = caseId;
      if (defaultClientId) body.clientId = defaultClientId;

      const res = await fetch('/api/invoices/from-unbilled', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to create invoice'));
      return res.json();
    },
    onSuccess: (json) => {
      toast.success('Invoice created from unbilled items');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['unbilled-approved'] });
      queryClient.invalidateQueries({ queryKey: ['expense-approvals'] });
      onClose();
      const invoiceId = json.data?.invoice?.id;
      if (invoiceId) {
        navigate('/invoices');
      }
    },
    onError: (err: Error) => toast.error(toUserErrorMessage(err, 'Failed to create invoice')),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Create invoice from unbilled items</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            Only <strong>approved</strong> billable items appear here. Pending expenses must be
            approved first on the Expense Approvals queue.
          </Alert>

          <CaseAutocomplete
            label="Filter by case (optional)"
            placeholder="All cases — search to filter…"
            value={caseId || null}
            onChange={(c) => {
              setCaseId(c?.id || '');
              setSelectedIds([]);
            }}
          />

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : error ? (
            <Alert severity="error">Unable to load unbilled items.</Alert>
          ) : selectable.length === 0 ? (
            <Alert severity="warning">
              No approved unbilled items
              {caseId ? ' for this case' : ''}. Approve expenses or add billable time first.
            </Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox checked={allSelected} onChange={toggleAll} />
                  </TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Case</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectable.map((item: any) => (
                  <TableRow key={item.id} hover selected={selectedIds.includes(item.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(item.id)}
                        onChange={() => toggleOne(item.id)}
                      />
                    </TableCell>
                    <TableCell>
                      {item.date
                        ? format(parseISO(String(item.date).slice(0, 10)), 'MMM d, yyyy')
                        : '—'}
                    </TableCell>
                    <TableCell>{item.type}</TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell>{item.case?.caseNumber || '—'}</TableCell>
                    <TableCell align="right">
                      $
                      {Number(item.amount || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Typography variant="body2" color="text.secondary">
            Selected: {selectedIds.length} · Total: $
            {selectedTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </Typography>

          <TextField
            label="Invoice notes (optional)"
            fullWidth
            multiline
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={selectedIds.length === 0 || createMutation.isPending}
          onClick={() => createMutation.mutate()}
        >
          {createMutation.isPending ? 'Creating…' : 'Create draft invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateFromUnbilledDialog;
